import type { EnemyDefinition } from "../domain/types";
import { BALANCE } from "./balance";

const visual = (accentColor: number, bobAmount = 1.4, shadowScale = 1) => ({
  runFrames: ["run-a", "run-b"],
  hitFrame: "hit",
  deathFrames: ["fall", "fallen"],
  shadowScale,
  bobAmount,
  accentColor,
});
const timing = (
  spawn: number = BALANCE.defaultSpawnDurationMs,
  death: number = BALANCE.defaultDeathDurationMs,
) => ({
  spawnDurationMs: spawn,
  spawnProtectionMs: spawn,
  deathDurationMs: death,
  escapeDurationMs: BALANCE.defaultEscapeDurationMs,
});
const base = (
  id: string,
  displayName: string,
  maximumHealth: number,
  movementSpeed: number,
  rewardValue: number,
  escapeValue: number,
  color: number,
  tags: string[],
  extra: Partial<EnemyDefinition> = {},
): EnemyDefinition => ({
  id,
  displayName,
  texture: `enemy-${id}`,
  maximumHealth,
  movementSpeed,
  armor: 0,
  resistances: {},
  escapeValue,
  rewardValue,
  size: 0.72,
  tags: ["ground", ...tags],
  deathEffect: "dust-death",
  exitEffect: "gate-breach",
  ...timing(),
  visual: visual(color),
  movementClass: "ground",
  ...extra,
});

const list: EnemyDefinition[] = [
  base("footman", "Footman", 70, 2.15, 12, 1, 0xb83d32, ["human"]),
  base("scout", "Scout", 48, 3.45, 14, 1, 0xe0a33b, ["fast"], { size: 0.58, ...timing(420, 440) }),
  base(
    "gilded-raider",
    "Gilded Raider",
    760,
    4.15,
    225,
    3,
    0xe3bd45,
    ["miniboss", "fast", "elite"],
    {
      armor: 0.1,
      resistances: { slow: 0.45, stun: 0.55 },
      size: 0.76,
      ...timing(560, 700),
    },
  ),
  base("knight", "Armored Knight", 250, 1.45, 35, 2, 0x8ca4b5, ["armored"], {
    armor: 0.32,
    resistances: { physical: 0.22 },
    size: 0.82,
  }),
  base("guard", "Royal Guard", 420, 1.85, 52, 2, 0x7f2635, ["elite"], {
    armor: 0.18,
    resistances: { slow: 0.55 },
    size: 0.86,
  }),
  base("siege", "Siege Runner", 1400, 0.92, 130, 4, 0x55453a, ["boss", "armored"], {
    armor: 0.42,
    resistances: { slow: 0.8, stun: 0.9, explosive: 0.15 },
    size: 1.05,
    ...timing(700, 780),
  }),
  base("militia", "Militia Swarm", 34, 2.6, 6, 1, 0xa86f45, ["swarm"], { size: 0.48 }),
  base("shield-bearer", "Shield Bearer", 190, 1.75, 28, 2, 0x607d8b, ["shield"], {
    shield: 110,
    armor: 0.12,
  }),
  base("berserker", "Berserker Runner", 185, 2.15, 30, 2, 0xad4b32, ["threshold-speed"]),
  base("regenerator", "Regenerator", 360, 1.45, 48, 2, 0x4e8c57, ["regeneration"], {
    regenerationPerSecond: 12,
  }),
  base("frostbound", "Frostbound", 310, 1.55, 44, 2, 0x87b9d8, ["frost"], {
    resistances: { frost: 0.8, slow: 0.65, fire: -0.2 },
  }),
  base("flame-warden", "Flame Warden", 330, 1.65, 46, 2, 0xcf6634, ["fire"], {
    resistances: { fire: 0.85, frost: -0.2 },
  }),
  base("venom-guard", "Venom Guard", 430, 1.35, 54, 2, 0x55793d, ["poison"], {
    resistances: { poison: 0.9 },
    armor: 0.1,
  }),
  base("arcane-sentinel", "Arcane Sentinel", 390, 1.5, 58, 2, 0x7457a1, ["arcane"], {
    resistances: { arcane: 0.75, electrical: 0.35 },
    armor: 0.04,
  }),
  base("splitter", "Splitter", 210, 1.9, 22, 1, 0xa87559, ["split"], {
    splitInto: [{ enemyId: "militia", count: 2 }],
  }),
  base("standard-bearer", "Standard Bearer", 300, 1.7, 65, 3, 0xb19742, ["aura"], {
    aura: { kind: "speed", radius: 2.5, magnitude: 0.18 },
  }),
  base("shadow-runner", "Shadow Runner", 150, 2.8, 38, 2, 0x463954, ["stealth"], { stealth: true }),
  base("tunnel-runner", "Tunnel Runner", 280, 2.1, 44, 2, 0x6c5038, ["tunnel"], {
    movementClass: "tunnel",
  }),
  base("winged-raider", "Winged Raider", 175, 2.65, 42, 2, 0x8d725f, ["flying"], {
    movementClass: "flying",
    tags: ["flying", "air"],
  }),
  base("war-mammoth", "War Mammoth", 2400, 0.72, 180, 6, 0x62564b, ["boss", "armored"], {
    armor: 0.36,
    resistances: { slow: 0.88, stun: 0.95 },
    size: 1.18,
  }),
  base("crown-champion", "Crown Champion", 4200, 1.05, 350, 10, 0xc0a348, ["boss", "champion"], {
    armor: 0.28,
    shield: 700,
    bossStages: [
      { healthRatio: 0.66, speedMultiplier: 1.15, shield: 350 },
      { healthRatio: 0.33, speedMultiplier: 1.3, shield: 200 },
    ],
    size: 1.1,
    ...timing(800, 900),
  }),
];
export const ENEMIES: Readonly<Record<string, EnemyDefinition>> = Object.freeze(
  Object.fromEntries(list.map((e) => [e.id, e])),
);
